import { NgClass } from '@angular/common';
import { AfterViewInit, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { LazyLoadEvent, MessageService, PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { Observable, Subject, Subscription } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../../../../core/configs/service-endpoints';
import { ServiceResult } from '../../../../../core/interfaces/service-result';
import { KeyToIconPipe } from '../../../../../core/pipes/key-to-icon.pipe';
import { AuthService } from '../../../../../core/services/auth.service';
import { HelperService } from '../../../../../core/services/helper.service';
import { I18nService } from '../../../../../core/services/i18n.service';
import { UserSessionSettingsProvider } from '../../../../../core/services/user-session-settings-provider.service';
import { UserService } from '../../../../../core/services/user.service';
import { SpsCustomIconComponent } from '../../../../../custom/components/sps-custom-icon/sps-custom-icon.component';
import { SpsDataViewTableComponent } from '../../../../../custom/components/sps-data-view-table/sps-data-view-table.component';
import { SpsPaymentStatusTagComponent } from '../../../../../custom/components/sps-payment-status-tag/sps-payment-status-tag.component';
import { SpsTableComponent } from '../../../../../custom/components/sps-table/sps-table.component';
import { TableConfig } from '../../../../../custom/interfaces/sps-table/table-config';
import { TableListProvider } from '../../../../../custom/provider/table-list-provider';
import { TableConfigService } from '../../../../../custom/services/table-config.service';
import { TableControlService } from '../../../../../custom/services/table-control.service';
import { CustomerGroup } from '../../../../enums/organisation/customer-group';
import { DialogTableDataRow } from '../../../../interfaces/dialog-table-data-row';
import { OrganisationData } from '../../../../interfaces/organisation/organisation-data';
import { PaymentData } from '../../../../interfaces/payment/payment-data';
import { UserData } from '../../../../interfaces/user-data';
import { ShopIntegrationToTableDataMapper } from '../../../../mapper/payment/shop-integration-to-table-data-mapper.service';
import { CustomerStatusTagPipe } from '../../../../pipes/customer-status-tag.pipe';
import { CustomerDataService } from '../../../../services/customer-data.service';
import { PaymentDataService } from '../../../../services/payment-data.service';
import { BaseComponent } from '../../../base/base.component';

@Component({
  selector: 'db-business-partner',
  imports: [
    Card,
    PrimeTemplate,
    SpsTableComponent,
    Dialog,
    Tag,
    TableModule,
    KeyToIconPipe,
    SpsCustomIconComponent,
    NgClass,
    Button,
    CustomerStatusTagPipe,
    SpsDataViewTableComponent,
    SpsPaymentStatusTagComponent,
    Tooltip
  ],
  templateUrl: './db-business-partner.component.html'
})
export class DbBusinessPartnerComponent extends BaseComponent implements OnInit, AfterViewInit {

  authService = inject(AuthService);
  i18n = inject(I18nService);
  private helper = inject(HelperService);
  private messageService = inject(MessageService);
  private tableConfigService = inject(TableConfigService);
  tableControlService = inject(TableControlService);
  private userSessionSettingsProvider = inject(UserSessionSettingsProvider);
  customerDataService = inject(CustomerDataService);
  private paymentDataService = inject(PaymentDataService);
  private userService = inject(UserService);
  private techDataMapper = inject(ShopIntegrationToTableDataMapper)

  /**
   * Tech-Permissions
   */
  userHasTechRole: boolean = this.authService.userHasRole('TECH');

  /**
   * table-config for transactions view -> SPS_TRANSACTIONS_VIEW.json
   */
  readonly tableConfig: WritableSignal<TableConfig> = signal(null);
  totalRecords: number = 0;
  rows: number = 20;
  scrollHeightOffset: number = 146;
  minScrollHeight: number = 160;
  tableListProvider: TableListProvider;
  selectedRowData: OrganisationData = null;
  lastLazyLoadingEvent: LazyLoadEvent = null;

  /**
   * Details-Dialog
   */
  showOrganisationDetailsDialog: boolean = false;
  organisationPaymentTypes: PaymentData[] = [];
  organisationPaymentTypesFetched = new Subject<boolean>()
  selectedPaymentType: PaymentData = null;
  selectedOrgaContactPerson: UserData = null;

  /**
   * Payment-Type tech data dialog
   */
  showPaymentTypeTechData: boolean = false;
  tableDataPaymentTypeTechSpecs: DialogTableDataRow[] = [];



  /**
   * SPS-Table-Component -> Reference
   * Initialized in HTML by event
   * needed for the data get event of the table in the polling-service
   * component.tableComponent.getTablePageData(component.lastLazyLoadingEvent);
   */
  tableComponent: SpsTableComponent = null;

  constructor() {
    super();
  }

  async ngOnInit() {
    this.loading = true;
    this.tableListProvider = new TableListProvider(this.i18n);
    this.tableConfig.set(await this.tableConfigService.getTableConfig('SPS_SPKRZ_ORGANISATIONS_VIEW', false));
    this.rows = this.tableConfig().rows;
    if (this.userSessionSettingsProvider.hasValue('SPKRZ_ORGANISATIONS_VIEW_ROWS')) {
      this.rows = this.userSessionSettingsProvider.get('SPKRZ_ORGANISATIONS_VIEW_ROWS');
    }
    this.loading = false;
  }

  ngAfterViewInit() {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.tableControlService.refreshTableConfig.set(true);
          this.refreshControls = true;
          setTimeout(() => {
            this.tableControlService.refreshTableConfig.set(false);
            this.refreshControls = false;
          }, 10);
        }
      })
    );
  }

  /**
   * Parent-Data-Provider
   * @param lazyLoadEvent
   */
  getOrganisationPageData = (lazyLoadEvent: LazyLoadEvent): Observable<ServiceResult> => {
    this.lastLazyLoadingEvent = lazyLoadEvent;
    return this.customerDataService.findPage(lazyLoadEvent, SERVICE_ENDPOINTS.GET_ORGANISATIONS_LEAD);
  };

  /**
   * DataMapper for table-data
   * @param serviceResult
   */
  organisationDataMapper = (serviceResult: ServiceResult) => {
    // TODO - map if needed
    return serviceResult;
  };

  onDataFetched(serviceResult: ServiceResult) {
    this.totalRecords = serviceResult.totalElements;
  }

  /**
   * Show details dialog if row is selected
   * @param organisationData
   */
  onSelectionChanged(organisationData: OrganisationData) {
    this.selectedRowData = null;
    if (organisationData) {
      this.selectedRowData = organisationData;
      this.showDetailsDialog();
    }
  }


  /**
   * Payment type in details dialog was selected
   * @param event
   * @protected
   */
  protected onPaymentRowSelected(event: TableRowSelectEvent<PaymentData>) {
    this.tableDataPaymentTypeTechSpecs = this.techDataMapper.map(this.selectedPaymentType, this.selectedRowData.displayCustomerNumber);
    this.showPaymentTypeTechData = true;
  }

  /**
   * fetch data for details dialog and show dialog
   * @private
   */
  private showDetailsDialog() {
    this.selectedOrgaContactPerson = null;
    /**
     * currently no primary contact should be shown
     */
    this.getPaymentTypes();
    /*
    const serviceEndpoint = SERVICE_ENDPOINTS.GET_USERS_ORG_INIT + this.selectedRowData.zbvOrganisationId + '/initial-user';
    this.userService.findAll(serviceEndpoint).subscribe({
      next: result => {
        if (result.successful) {
          this.selectedOrgaContactPerson = result.dto;
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
        this.getPaymentTypes();
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
        this.getPaymentTypes();
      }
    });
     */
  }

  /**
   * get payment types of the organisation
   * for the details dialog
   * @private
   */
  private getPaymentTypes() {
    const subscription: Subscription = this.getCustomerPaymentTypes().subscribe({
      next: result => {
        subscription.unsubscribe();
        this.showOrganisationDetailsDialog = true;
      }
    })
  }

  /**
   * fetch the payment types for the selected organisation
   * @protected
   */
  protected getCustomerPaymentTypes(): Observable<boolean> {
    this.organisationPaymentTypes = [];
    this.paymentDataService.findPageAdmin(this.helper.eagerLazyLoadEvent(), this.selectedRowData.zbvOrganisationId).subscribe({
      next: result => {
        if (result.successful) {
          this.organisationPaymentTypes = result.payload;
          this.organisationPaymentTypesFetched.next(true);
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
          this.organisationPaymentTypesFetched.next(false);
        }
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
        this.organisationPaymentTypesFetched.next(false);
      }
    })
    return this.organisationPaymentTypesFetched.asObservable();
  }

  /**
   * Close Organisation Details Dialog
   */
  closeDetailsDialog() {
    this.showOrganisationDetailsDialog = false;
    this.selectedRowData = null;
    this.tableControlService.unselectTableRows.set(true);
  }

  /**
   * Rows changed by user
   * @param newRowCount
   */
  rowsCountChanged(newRowCount: number) {
    this.rows = newRowCount;
    this.userSessionSettingsProvider.set('SPKRZ_ORGANISATIONS_VIEW_ROWS', this.rows);
  }

  /**
   * close the payment-type tech data dialog
   */
  closeTechDataDialog() {
    this.selectedPaymentType = null;
    this.showPaymentTypeTechData = false;
  }

  /**
   * Clear all Table Filters and Sorting and reset the table state and the selection
   * @protected
   */
  protected clearTableFilters() {
    this.tableControlService.clearState.set(true);
    this.tableControlService.clearTableFilters.set(true);
    this.onSelectionChanged(null);
  }

  protected readonly CustomerGroup = CustomerGroup;
}
